import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { PolicyConditionKey } from "features/RolesAndPermissions/constants/policy.constants";
import { usePendingMaterialRequestCount } from "features/MaterialRequests/hooks/usePendingMaterialRequestCount";
import { setPendingMaterialRequestCountRefetch } from "features/MaterialRequests/materialRequestPendingCountInvalidate";

export type MaterialRequestPendingCountContextValue = {
  count: number;
  isLoading: boolean;
  refetch: () => void;
};

const MaterialRequestPendingCountContext =
  createContext<MaterialRequestPendingCountContextValue | null>(null);

const noopRefetch = () => {};

const materialRequestPendingCountFallback: MaterialRequestPendingCountContextValue =
  {
    count: 0,
    isLoading: false,
    refetch: noopRefetch,
  };

export function MaterialRequestPendingCountProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { canWithPolicy } = useAccess();
  const enabled = canWithPolicy(
    Permissions.MATERIAL_REQUEST.READ,
    PolicyConditionKey.ALL,
  );
  const { count, isLoading, refetch } = usePendingMaterialRequestCount(enabled);

  useEffect(() => {
    if (!enabled) {
      setPendingMaterialRequestCountRefetch(null);
      return;
    }
    setPendingMaterialRequestCountRefetch(refetch);
    return () => {
      setPendingMaterialRequestCountRefetch(null);
    };
  }, [enabled, refetch]);

  const value = useMemo(
    () => ({ count, isLoading, refetch }),
    [count, isLoading, refetch],
  );

  return (
    <MaterialRequestPendingCountContext.Provider value={value}>
      {children}
    </MaterialRequestPendingCountContext.Provider>
  );
}

export function useMaterialRequestPendingCount(): MaterialRequestPendingCountContextValue {
  const ctx = useContext(MaterialRequestPendingCountContext);
  if (!ctx) {
    throw new Error(
      "useMaterialRequestPendingCount must be used within MaterialRequestPendingCountProvider",
    );
  }
  return ctx;
}

/**
 * For menu builders (e.g. `Navdata`) that may be invoked unconventionally: returns
 * a stable fallback when no provider is present instead of throwing.
 */
export function useMaterialRequestPendingCountOptional(): MaterialRequestPendingCountContextValue {
  const ctx = useContext(MaterialRequestPendingCountContext);
  return ctx ?? materialRequestPendingCountFallback;
}
